/**
 * Which install instructions fit the device in hand.
 *
 * Only Chromium browsers offer an install prompt a page can trigger. Every
 * other case is a menu the member has to find, and the menu differs by
 * platform and browser, so the install page leads with the one that fits
 * and lists the rest beneath it. Pure, so it can be tested with the user
 * agents real devices send.
 */

export type InstallPlatform =
  | 'ios'
  | 'android'
  | 'mac-safari'
  | 'mac-chromium'
  | 'windows'
  | 'desktop-chromium'
  | 'desktop-firefox'
  | 'other';

export interface DeviceHints {
  userAgent: string;
  /** `navigator.maxTouchPoints`, which is how an iPad passes as a Mac. */
  maxTouchPoints: number;
}

export function detectPlatform({
  userAgent,
  maxTouchPoints,
}: DeviceHints): InstallPlatform {
  const ua = userAgent;
  // iPadOS asks for desktop sites and reports itself as a Mac; a Mac has
  // no touch screen, so touch points give it away.
  const ipad = /Macintosh/.test(ua) && maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || ipad) return 'ios';
  if (/Android/.test(ua)) return 'android';
  const chromium = /Chrome\/|Chromium\/|Edg\//.test(ua);
  if (/Firefox\//.test(ua)) return 'desktop-firefox';
  if (/Macintosh/.test(ua)) return chromium ? 'mac-chromium' : 'mac-safari';
  if (/Windows/.test(ua)) return 'windows';
  if (chromium) return 'desktop-chromium';
  return 'other';
}

export interface InstallSteps {
  platform: InstallPlatform;
  title: string;
  steps: string[];
  note?: string;
}

export const INSTALL_STEPS: Record<
  Exclude<InstallPlatform, 'other'>,
  InstallSteps
> = {
  ios: {
    platform: 'ios',
    title: 'iPhone and iPad',
    steps: [
      'Open this page in Safari.',
      'Tap the Share button: the square with an arrow pointing up, at the bottom of the screen on iPhone or the top on iPad.',
      'Scroll down and tap Add to Home Screen.',
      'Tap Add. Happy Fishy appears on your home screen with its own icon.',
    ],
    note: 'Chrome and Edge on iPhone can do this too, from their own Share button, on iOS 16.4 and later.',
  },
  android: {
    platform: 'android',
    title: 'Android',
    steps: [
      'Open this page in Chrome.',
      'Tap Install Happy Fishy above, if it is shown. Otherwise tap the ⋮ menu at the top right.',
      'Tap Install app, or Add to home screen.',
      'Tap Install. Happy Fishy appears with your other apps.',
    ],
    note: 'Once installed on Android, photos and clips can be shared straight into a new entry from your gallery.',
  },
  'mac-safari': {
    platform: 'mac-safari',
    title: 'Mac, in Safari',
    steps: [
      'Open this page in Safari (macOS Sonoma or later).',
      'In the menu bar, choose File, then Add to Dock.',
      'Click Add. Happy Fishy opens from the Dock in its own window.',
    ],
  },
  'mac-chromium': {
    platform: 'mac-chromium',
    title: 'Mac, in Chrome or Edge',
    steps: [
      'Click Install Happy Fishy above, if it is shown.',
      'Otherwise click the install icon at the right-hand end of the address bar: a screen with a down arrow.',
      'Or open the ⋮ menu (… in Edge), then Cast, save and share (Apps in Edge), then Install page as app.',
      'Click Install. Happy Fishy opens in its own window and appears in Launchpad.',
    ],
  },
  windows: {
    platform: 'windows',
    title: 'Windows, in Edge or Chrome',
    steps: [
      'Click Install Happy Fishy above, if it is shown.',
      'Otherwise click the install icon at the right-hand end of the address bar: a screen with a down arrow, or a box with a plus in Edge.',
      'Or open the … menu in Edge, then Apps, then Install this site as an app. In Chrome: ⋮, then Cast, save and share, then Install page as app.',
      'Click Install. Happy Fishy gets a Start menu entry, and you can pin it to the taskbar.',
    ],
  },
  'desktop-chromium': {
    platform: 'desktop-chromium',
    title: 'Chromebook or Linux, in Chrome or Edge',
    steps: [
      'Click Install Happy Fishy above, if it is shown.',
      'Otherwise click the install icon at the right-hand end of the address bar: a screen with a down arrow.',
      'Or open the ⋮ menu, then Cast, save and share, then Install page as app.',
      'Click Install. Happy Fishy appears with your other apps.',
    ],
  },
  'desktop-firefox': {
    platform: 'desktop-firefox',
    title: 'Firefox on a computer',
    steps: [
      'Firefox on a computer cannot install web apps.',
      'Open this page in Chrome or Edge, or in Safari on a Mac, and follow the steps for that browser.',
    ],
    note: 'Everything still works in Firefox as a website, including offline once it has loaded.',
  },
};

/** Running as the installed app rather than in a browser tab. */
export function isStandalone(win: {
  matchMedia?: (query: string) => { matches: boolean };
  navigator: object;
}): boolean {
  return (
    win.matchMedia?.('(display-mode: standalone)').matches === true ||
    // Safari on iPhone says so here rather than through display-mode, and
    // it is not in the DOM types.
    ('standalone' in win.navigator && win.navigator.standalone === true)
  );
}
